import { useState, useRef, useEffect } from "react";
import { useLanguage, Language } from "../../i18n";

type FlagProps = {
  lang: Language;
  size?: number;
};

// Neo-brutalist styled flag icons using SVG
const Flag = ({ lang, size = 20 }: FlagProps): JSX.Element => {
  const flags: Record<Language, JSX.Element> = {
    en: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        {/* UK Flag - Union Jack simplified */}
        <rect width="24" height="24" rx="3" fill="#012169" />
        <path d="M0 0L24 24M24 0L0 24" stroke="#fff" strokeWidth="4" />
        <path d="M0 0L24 24M24 0L0 24" stroke="#C8102E" strokeWidth="2" />
        <path d="M12 0V24M0 12H24" stroke="#fff" strokeWidth="6" />
        <path d="M12 0V24M0 12H24" stroke="#C8102E" strokeWidth="3" />
      </svg>
    ),
    fr: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="3" fill="#fff" />
        <rect width="8" height="24" fill="#002395" rx="3" />
        <rect x="16" width="8" height="24" fill="#ED2939" />
        <rect width="24" height="24" rx="3" stroke="#000" strokeWidth="1" fill="none" />
      </svg>
    ),
    de: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect width="24" height="8" rx="3" fill="#000" />
        <rect y="8" width="24" height="8" fill="#DD0000" />
        <rect y="16" width="24" height="8" fill="#FFCE00" />
        <rect width="24" height="24" rx="3" stroke="#000" strokeWidth="0.5" fill="none" />
      </svg>
    ),
    pl: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect width="24" height="12" rx="3" fill="#fff" />
        <rect y="12" width="24" height="12" fill="#DC143C" />
        <rect width="24" height="24" rx="3" stroke="#000" strokeWidth="0.5" fill="none" />
      </svg>
    ),
  };

  return (
    <span className="flag-icon" aria-hidden="true">
      {flags[lang]}
    </span>
  );
};

export const LanguageSwitcher = (): JSX.Element => {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: Language[] = ["en", "fr", "de", "pl"];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSelect = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  const getLanguageName = (lang: Language): string => {
    const names: Record<Language, string> = {
      en: t.language.en,
      fr: t.language.fr,
      de: t.language.de,
      pl: t.language.pl,
    };
    return names[lang];
  };

  return (
    <div className="lang-switcher" ref={dropdownRef}>
      <button
        type="button"
        className="lang-switcher-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${t.language.label}: ${getLanguageName(language)}`}
      >
        <Flag lang={language} size={22} />
        <span className="lang-current">{language.toUpperCase()}</span>
        <span className={`lang-arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>

      {isOpen && (
        <ul className="lang-dropdown" role="listbox" aria-label={t.language.label}>
          {languages.map((lang) => (
            <li key={lang} role="option" aria-selected={lang === language}>
              <button
                type="button"
                className={`lang-option ${lang === language ? "active" : ""}`}
                onClick={() => handleSelect(lang)}
              >
                <Flag lang={lang} size={20} />
                <span className="lang-name">{getLanguageName(lang)}</span>
                {lang === language && <span className="lang-check">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

